import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, ArrowLeft, Shield } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('polling'); // polling | paid | expired | error
  const [result, setResult] = useState(null);
  const polledRef = useRef(false);

  useEffect(() => {
    if (!sessionId || polledRef.current) return;
    polledRef.current = true;
    pollStatus(sessionId, 0);
  }, [sessionId]);

  const pollStatus = async (sid, attempt) => {
    const maxAttempts = 8;
    const interval = 2000;

    if (attempt >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/pricing/checkout/status/${sid}`);
      if (!res.ok) {
        setStatus('error');
        return;
      }
      const data = await res.json();

      if (data.payment_status === 'paid') {
        setStatus('paid');
        setResult(data);
        return;
      }
      if (data.payment_status === 'expired') {
        setStatus('expired');
        return;
      }

      // Still pending, poll again
      setTimeout(() => pollStatus(sid, attempt + 1), interval);
    } catch {
      if (attempt < maxAttempts - 1) {
        setTimeout(() => pollStatus(sid, attempt + 1), interval);
      } else {
        setStatus('error');
      }
    }
  };

  if (!sessionId) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <XCircle className="w-12 h-12 text-red-400 mx-auto" />
            <h2 className="text-lg font-semibold text-slate-700">No session found</h2>
            <p className="text-sm text-slate-500">This page requires a valid payment session.</p>
            <Button onClick={() => navigate('/admin/pricing')} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Pricing
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-[60vh]">
      <Card className="max-w-lg w-full" data-testid="payment-result-card">
        <CardContent className="p-8 text-center space-y-5">

          {status === 'polling' && (
            <>
              <Loader2 className="w-12 h-12 text-sky-500 mx-auto animate-spin" />
              <h2 className="text-lg font-semibold text-slate-700">Verifying Payment...</h2>
              <p className="text-sm text-slate-500">
                Please wait while we confirm your payment with Stripe.
              </p>
            </>
          )}

          {status === 'paid' && (
            <>
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-emerald-700" data-testid="payment-success-title">
                Payment Successful!
              </h2>
              <p className="text-sm text-slate-600">
                You are now subscribed to <span className="font-semibold">{result?.tier_name}</span>.
              </p>
              {result?.modules?.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-4 text-left">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Shield className="w-3 h-3" /> Activated Modules
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {result.modules.map((m) => (
                      <span key={m} className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                        {m}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {result?.subscription_id && (
                <p className="text-xs text-slate-400">Subscription ID: {result.subscription_id}</p>
              )}
              <Button onClick={() => navigate('/dashboard')} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Go to Dashboard
              </Button>
            </>
          )}

          {status === 'expired' && (
            <>
              <XCircle className="w-12 h-12 text-amber-500 mx-auto" />
              <h2 className="text-lg font-semibold text-amber-700">Payment Session Expired</h2>
              <p className="text-sm text-slate-500">Your checkout session has expired. Please try again.</p>
              <Button onClick={() => navigate('/admin/pricing')} variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Pricing
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-12 h-12 text-red-400 mx-auto" />
              <h2 className="text-lg font-semibold text-red-700">Verification Timeout</h2>
              <p className="text-sm text-slate-500">
                We couldn't verify your payment yet. If you completed the payment, your subscription will be activated shortly.
              </p>
              <Button onClick={() => navigate('/admin/pricing')} variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Pricing
              </Button>
            </>
          )}

        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
